import { parseCoils } from './utils';
import { categoryKey } from '../ui-components/category/categoryColors';
import { LinkedPolicyDTO } from '../../services/apiService';
import {
    MRIMachinePopulated,
    MachineFilterOptions,
    MachineFilterState,
    NO_LOCATION,
    NO_CATEGORY,
} from './types';

export interface LocationSection {
    key: string;
    machines: MRIMachinePopulated[];
}

const byName = (a: string, b: string) =>
    a.localeCompare(b, undefined, { sensitivity: 'base' });

export const buildFilterOptions = (
    machines: MRIMachinePopulated[]
): MachineFilterOptions => {
    const fieldStrengths = [
        ...new Set(machines.map((m) => m.FieldStrength)),
    ].sort((a, b) => a - b);
    const stripCoilSuffix = (c: string) => c.replace(/\s*coil$/i, '').trim();
    const coilTypes = machines
        .flatMap((m) => parseCoils(m.TRCoils))
        .map((c) => stripCoilSuffix(c))
        .filter(
            (coil, i, arr) =>
                arr.findIndex((c) => c.toLowerCase() === coil.toLowerCase()) ===
                i
        )
        .sort(byName);
    const maxSpatialGradient = Math.max(
        ...machines.flatMap(
            (m) => m.SpatialGradient?.map((sg) => sg.value) || [0]
        ),
        10
    );
    const locations = [
        ...new Set(machines.flatMap((m) => (m.location ? [m.location] : []))),
    ].sort(byName);
    const categories = machines
        .flatMap((m) => (m.category ? [m.category] : []))
        .filter(
            (c, i, arr) =>
                arr.findIndex((o) => categoryKey(o) === categoryKey(c)) === i
        )
        .sort((a, b) => byName(a.name, b.name));

    return {
        fieldStrengths,
        models: [...new Set(machines.map((m) => m.Model))].sort(),
        maxSpatialGradient,
        coilTypes,
        locations,
        categories,
    };
};

export const filterMachines = (
    machines: MRIMachinePopulated[],
    searchTerm: string,
    filters: MachineFilterState,
    favoritedMachineIds: Set<string>
): MRIMachinePopulated[] => {
    const term = searchTerm.toLowerCase().trim();

    return machines
        .filter((machine) => {
            if (term) {
                const matchesSearch =
                    machine.Name?.toLowerCase().includes(term) ||
                    machine.Model?.toLowerCase().includes(term) ||
                    machine.Manufacturer?.toLowerCase().includes(term) ||
                    machine.location?.toLowerCase().includes(term) ||
                    machine.category?.name?.toLowerCase().includes(term);
                if (!matchesSearch) return false;
            }

            if (filters.location) {
                const matchesLocation =
                    filters.location === NO_LOCATION
                        ? !machine.location
                        : machine.location === filters.location;
                if (!matchesLocation) return false;
            }

            if (filters.category) {
                const matchesCategory =
                    filters.category === NO_CATEGORY
                        ? !machine.category
                        : machine.category &&
                          categoryKey(machine.category) === filters.category;
                if (!matchesCategory) return false;
            }

            if (filters.fieldStrength?.length) {
                if (!filters.fieldStrength.includes(machine.FieldStrength)) {
                    return false;
                }
            }

            if (filters.coilType?.length) {
                const machineTRCoils = machine.TRCoils?.toLowerCase() || '';
                if (
                    !filters.coilType.some((coil) =>
                        machineTRCoils.includes(coil.toLowerCase())
                    )
                ) {
                    return false;
                }
            }

            if (filters.maxSpatialGradient !== undefined) {
                const maxGradient = Math.max(
                    ...(machine.SpatialGradient?.map((sg) => sg.value) || [0])
                );
                if (maxGradient > filters.maxSpatialGradient) {
                    return false;
                }
            }

            if (filters.favoritesOnly) {
                if (!favoritedMachineIds.has(machine._id)) {
                    return false;
                }
            }

            return true;
        })
        .sort((a, b) => {
            const aFav = favoritedMachineIds.has(a._id) ? 1 : 0;
            const bFav = favoritedMachineIds.has(b._id) ? 1 : 0;
            return bFav - aFav || byName(a.Name, b.Name);
        });
};

export const groupByLocation = (
    machines: MRIMachinePopulated[]
): LocationSection[] => {
    const byLocation = new Map<string, MRIMachinePopulated[]>();
    machines.forEach((machine) => {
        const key = machine.location || NO_LOCATION;
        byLocation.set(key, [...(byLocation.get(key) ?? []), machine]);
    });
    const located = [...byLocation.keys()]
        .filter((key) => key !== NO_LOCATION)
        .sort(byName);
    const keys = byLocation.has(NO_LOCATION)
        ? [...located, NO_LOCATION]
        : located;
    return keys.map((key) => ({ key, machines: byLocation.get(key)! }));
};

export interface RoomPolicy {
    policy: LinkedPolicyDTO;
    onRoom: boolean;
    items: string[];
}

export const roomPolicies = (machine: {
    policies?: LinkedPolicyDTO[];
    equipment?: { name: string; policies: LinkedPolicyDTO[] }[];
}): RoomPolicy[] => {
    const byId = new Map<string, RoomPolicy>();
    const add = (policy: LinkedPolicyDTO) => {
        const entry = byId.get(policy._id) ?? {
            policy,
            onRoom: false,
            items: [],
        };
        if (!entry.policy.document_id?.title && policy.document_id?.title) {
            entry.policy = policy;
        }
        byId.set(policy._id, entry);
        return entry;
    };
    machine.policies?.forEach((policy) => {
        add(policy).onRoom = true;
    });
    machine.equipment?.forEach((item) =>
        item.policies.forEach((policy) => add(policy).items.push(item.name))
    );
    return [...byId.values()];
};
